'use client';

/**
 * МОЗГ РОДНЫХ ОКОН КАРТОЧКИ БОТА (docs/NATIVE-TABS-PLAN.md, волна 3, зона «Боты»).
 *
 * Окна рисует натив (BotWindowScreen.swift), а этот кусок кабинета остаётся мозгом:
 * держит ТЕ ЖЕ данные и зовёт ТЕ ЖЕ ручки, что веб-шторки, которые он заменяет —
 *   • страница одного бота (bot-stats.tsx: три цифры, один график с капсулой периодов,
 *     итог и журнал СОБЫТИЙ ЛЮДЬМИ; у рассылки — её цифры, причины и кнопки запуска);
 *   • журнал запусков и деталь одного прогона (scenario-runs.tsx);
 *   • «О сценарии» (scenario-workspace.tsx: ScenarioAboutForm — общая рука сохранения);
 *   • «Создать дубликат» (scenario-duplicate.tsx — общая рука создания);
 *   • причины «не включился» и осечки «не выключился / не удалилось» (scenarios.tsx).
 *
 * INVARIANT(scriptera): веб-слоя у родных окон НЕТ — мозг живёт скрытым куском страницы,
 * с которой окно открыли (тот же приём, что у листа шаблонов, мастера и «Аналитики»).
 * Причина: открытый веб-слой стоял бы под родным экраном пустым листом со своей шапкой и
 * мелькал бы в момент закрытия. Канон: docs/NATIVE-TABS-PLAN.md, волна 2 п.4.
 *
 * Запасной путь: сборка метода `statsOpen` не имеет (старая установка из стора, Android) —
 * карточка открывает прежние веб-шторки, сюда не заходит.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { contactInitial, useContactCardHost } from './contacts';
import { hasNativeNotice, showNativeNotice } from './confirm-sheet';
// Карточка человека — ТА ЖЕ, что в разделе «Контакты» (второй карточки в кабинете нет).
import { CARD_LINE_SETS, cardSetKey, parseCardPrefs, weekAllowed, type CardLine, type CardPrefs } from '@/lib/bot-card-prefs';
import { draftFolders, toggleFolder } from '@/lib/bot-folder-set';
import { botFailTextKey } from '@/lib/bot-fail-text';
// Адрес — БЕЗ языка (usePathname next/navigation), как у хозяина «Аналитики»: с ним
// сравнивается window.location.pathname, снятый в момент открытия.
import { usePathname } from 'next/navigation';

import { BUILDER_HIDDEN } from '@/lib/hidden-features';
import { broadcastFailKey } from '@scriptera/ui/scenario';
import { broadcastModeOfRow, isBroadcastRow } from '@/lib/broadcast-list';
import { useNativeStats } from '@/lib/stats-native';
import type {
  NativeAccountField,
  NativeStatsBlock,
  NativeStatsSeries,
  NativeStatsFolders,
  NativeStatsCard,
  NativeStatsEvent,
  NativeStatsPage,
  NativeStatsRun,
} from '@/lib/native-app';
import type { NativeBotsRect } from '@/lib/bots-native';
import { EVENT_DAYS, EVENT_DAYS_DEFAULT, groupBotEvents, periodButtons, type EventDays } from '@/lib/bot-page-events';
import { usePersistentData } from '@/lib/use-persistent-data';
import { isNativeApp, nativeBridge, nativeMethod, readRoleHex } from '@/lib/native-app';
import { circleColor } from '@/lib/circle-color';
import { useFavorites } from '@/lib/use-favorites';
import { catList } from '@/lib/script-cats';
import { reportUiError } from '@/lib/ui-error-report';
import { log as journalLog, noteBridgeEvent } from '@/lib/client-journal';
import { useBotsFolders } from './bots-folders';
import { useBroadcastLaunch } from './broadcast-launch';
import { isAppClient } from './layout-mode';
import { setBotWindowBubbles } from '@/lib/bot-window-bubbles';
import {
  useBotEmptyFacts,
  useBotEvents,
  useBotEventWords,
  useBotPeriod,
  useBotStep,
  useContactRow,
  useOpenDialogFromEvent,
  warmContactRows,
  type EventsOnly,
} from './bot-journal';
import { channelHex } from './bot-templates';
import { LINE_INK } from '@/lib/quick-tiles';
import { CHAIN_WIZARD_ID } from '@/lib/template-wizards';
import { chainIsEmpty, chainSendName, useChainStats, warmChainStats } from './chain-page';
import { useChainToggle } from './chain-toggle';
import { chanOf } from './scenarios';
import { useNativePhotos } from './native-photos';
import { postScenarioDuplicate } from './scenario-duplicate';
import { putScenarioAbout } from './scenario-workspace';
import { useRunDetail, useRunFmt, useRunsChart, useRunsFeed, warmRunDetails, warmRuns } from './scenario-runs';
import { useScriptRowsState } from './split-menu';
import { useThemeAttr } from './settings';
import type { DraftSnapshot } from './chat';

/** Какое окно карточки открыто. Один экран на все — тела разные, механизм один. */
export type BotWindow =
  /** Страница одного бота: цифры, график недели, «Редактировать», журнал запусков. */
  | { kind: 'stats'; rowKey: string }
  /** Журнал запусков отдельным окном: пункт «Запуски» и чип «Сбои: N за сутки». */
  | { kind: 'runs'; workflowId: string; onlyErrors: boolean }
  /** «О сценарии» из «⋯». */
  | { kind: 'about'; draftId: string }
  /** «Создать дубликат» из «⋯». */
  | { kind: 'dup'; draftId: string; title: string; wizard: boolean }
  /** Почему бот не включился (чип «Не включился» и сам отказ включения). */
  | { kind: 'fail'; fail: 'lint' | 'balance' | 'tech'; href: string | null }
  /** Осечка выключения или удаления: заголовок и одна честная строка. */
  | { kind: 'note'; title: string; text: string };

/** Руки раздела, которые окну нужны: переход в бота и перечитывание списка. */
export interface BotWindowHooks {
  goScn?: (href: string, wizId?: string | null) => void;
  /** «⋯» шапки страницы бота — ТО ЖЕ родное меню, что у карточки в списке (П42): состав
   *  и руки пунктов живут у раздела (scenarios.tsx, cardMenuOf), окно только показывает
   *  меню у рамы нажатой кнопки. Руки нет — «⋯» не рисуем вовсе. */
  menu?: (rect: NativeBotsRect) => void;
  /** ТУМБЛЕР «ВКЛЮЧЁН» НА СТРАНИЦЕ БОТА (П158 п.2): ТА ЖЕ рука, что «Включить/Выключить»
   *  карточки в списке «Ботов» (scenarios.tsx, `onToggle` меню): подтверждение, проверки,
   *  плашка неоплаты, отказ родным листом с вибрацией. Ответ — когда попытка кончилась
   *  (в том числе «Отмена»). Руки нет — тумблера на странице нет. */
  toggle?: () => unknown;
  reload?: () => void;
  reloadPulse?: () => void;
  /** APP-CLAIMS-1020 ред. 6, П20: «Редактировать» в отчёте рассылки — та же рука, что пункт меню карточки. */
  edit?: () => void;
  /** Копия создана — подсветить новую карточку той же вспышкой, что у нового бота. */
  fresh?: (id: string) => void;
}

interface HostState {
  /** Свой номер каждого открытия: перемонтирует мозг, а не донашивает прошлый. */
  key: number;
  /** Страница, с которой окно открыли: ушли с неё — оно закрывается. */
  path: string;
  win: BotWindow;
  hooks: BotWindowHooks;
}

let seq = 0;
let cur: HostState | null = null;
let listener: ((s: HostState | null) => void) | null = null;

/** Открыть родное окно карточки (экран поднимает натив, мозг встаёт скрытым). */
export function openNativeBotWindow(win: BotWindow, hooks: BotWindowHooks = {}): void {
  seq += 1;
  cur = { key: seq, path: typeof window === 'undefined' ? '' : window.location.pathname, win, hooks };
  listener?.(cur);
}

/** Убрать окно: мозг размонтируется и сам зовёт statsClose. */
export function closeNativeBotWindow(): void {
  cur = null;
  listener?.(null);
}

/** СТРАНИЦА ПОВЕРХ ОТКРЫТОГО ОКНА (П98/П103, владелец 13.09.2026: «где открыл, там сверху
 *  открыл; зачем закрывать?»): «О боте» и «Создать дубликат» из «⋯» на странице бота
 *  встают страницами стопки этого же окна. Окна нет — false, и раздел открывает как из
 *  списка. */
const BOT_WINDOW_PAGE_EVENT = 'scr-bot-window-page';
export function pushNativeBotWindowPage(page: 'about' | 'dup'): boolean {
  if (!cur || typeof window === 'undefined') return false;
  window.dispatchEvent(new CustomEvent(BOT_WINDOW_PAGE_EVENT, { detail: { page } }));
  return true;
}

/**
 * ЗАКРЫТЬ ОКНО БОТА И ТОЛЬКО ПОТОМ ИДТИ ДАЛЬШЕ (Н45, владелец 03.09.2026, запись экрана:
 * «Редактировать» со страницы бота закрывала страницу, а мастер не открывался). Окно —
 * системный лист; свежая сборка отвечает на statsClose, когда лист УЕХАЛ, и лишь после
 * этого анкета просит показать себя (показ поверх уезжающего листа UIKit отбрасывает).
 * Старая сборка отвечает сразу — там всё как было. Хозяина снимаем после ответа: его
 * прибор (useNativeStats) зовёт statsClose ещё раз на размонтировании — окна уже нет,
 * натив отвечает сразу.
 */
/**
 * УЙТИ ИЗ ОКНА БОТА ПРЯМО В ЧАТ — ОДНИМ ДВИЖЕНИЕМ (владелец 14.09.2026: «чат открывается
 * через несколько действий, типа закрыта шторка и прочая хуета; хочу, чтоб сразу приехала
 * горизонтальная шторка, а закрылась на странице „Чаты“»).
 *
 * Порядок обратный обычному: СНАЧАЛА просьба открыть переписку и переход на вкладку
 * «Чаты», и только потом — закрытие окна бота. Раздел «Чаты» поднимает свой экран сразу за
 * уходом листа, и человек видит один ход, а не «закрылось — подождал — открылось».
 */
export async function openChatFromBotWindow(open: () => void): Promise<void> {
  open();
  closeNativeBotWindow();
  if (nativeMethod('statsClose')) {
    try {
      await nativeBridge()?.statsClose?.();
    } catch {
      /* мост промолчал — человека не держим */
    }
  }
}

export async function closeBotWindowThen(next: () => void): Promise<void> {
  if (nativeMethod('statsClose')) {
    try {
      await nativeBridge()?.statsClose?.();
    } catch {
      /* мост промолчал — держать человека нельзя, идём дальше */
    }
  }
  closeNativeBotWindow();
  next();
}

/**
 * БОТ УДАЛЁН — ЕГО ОКНО ЗАКРЫВАЕТСЯ (П165, владелец 27.09.2026, скрин 10:09: после
 * удаления окно осталось с серой строкой «Бот удалён.»). Зовёт рука удаления раздела
 * (scenarios.tsx, removeDraft) после ответа сервера: открыто окно именно этого бота —
 * лист уезжает, список «Ботов» перечитывается без строки. Страница «Бот удалён.»
 * (`statsGone`) остаётся только для окна, открытого по старой ссылке/пушу на уже
 * удалённого бота.
 */
export async function closeBotWindowOfBot(draftId: string): Promise<void> {
  if (cur?.win.kind !== 'stats' || cur.win.rowKey !== draftId) return;
  await closeBotWindowThen(() => {});
}

/** Хозяин: стоит один на кабинет, рядом с хозяином «Аналитики» (shell.tsx). */
export function BotWindowNativeHost() {
  const [st, setSt] = useState<HostState | null>(cur);
  const path = usePathname();
  useEffect(() => {
    listener = setSt;
    return () => {
      listener = null;
    };
  }, []);
  // Ушли со страницы (тап по вкладке, переход в бота) — окно принадлежит той странице,
  // на которой его открыли.
  useEffect(() => {
    if (st && st.path && path && st.path !== path) closeNativeBotWindow();
  }, [path, st]);
  if (!st) return null;
  return <BotWindowDriver key={st.key} win={st.win} hooks={st.hooks} />;
}

/** Ключ корневой страницы окна — он же ключ возврата «закрыли окно». */
const ROOT = 'root';

function BotWindowDriver({ win, hooks }: { win: BotWindow; hooks: BotWindowHooks }) {
  const t = useTranslations('cabinet.automations');
  const to = useTranslations('cabinet.overview');
  const ts = useTranslations('cabinet.scenario');
  const tb = useTranslations('cabinet.broadcasts');
  // «Контакты» — там же живут слова «Открыть переписку» и «Отмена» для листа меню.
  const tct = useTranslations('cabinet.contacts');
  // Папки «Ботов» в «Настройках» — там слово свайпа «Удалить» (лист папки, П158 п.4).
  const tf = useTranslations('cabinet.automations.folders');
  const locale = useLocale();
  const [themeAttr] = useThemeAttr();
  const { fmtDuration, fmtStepDuration, fmtWhen, fmtTime, fmtDay, statusLabel, modeLabel, whyText } =
    useRunFmt();
  const { rows, loading: rowsLoading } = useScriptRowsState();
  const { favs: cats, setList: setCatList } = useFavorites('scriptCategories');

  /** Что открыто ПОВЕРХ корня (деталь прогона). Натив уже открыл страницу пальцем. */
  const [stack, setStack] = useState<string[]>([]);
  const open = stack[stack.length - 1] ?? '';
  const openRunId = open.startsWith('run:') ? open.slice(4) : '';
  /** Имя новой категории на странице `about:newcat` (П102). */
  const [newCat, setNewCat] = useState('');

  const row = win.kind === 'stats' ? (rows.find((r) => r.key === win.rowKey || r.draftId === win.rowKey) ?? null) : null;
  const bcast = row ? isBroadcastRow(row) : false;
  const ctl = useBroadcastLaunch(bcast && row ? (row.draftId ?? row.key) : '');
  // Р2 ред. 7: письмо рассылки — веб-окну (первым на отчёте, пузырями «Чатов»); мимо моста — см. lib/bot-window-bubbles.
  const bcMessages = bcast ? ctl.state?.messages : undefined;
  useEffect(() => {
    if (isAppClient()) return;
    setBotWindowBubbles(bcMessages);
    return () => setBotWindowBubbles(null);
  }, [bcMessages]);
  // ЦЕПОЧКА — СВОЯ СТРАНИЦА (владелец 16.09.2026, М1.6): у неё не бывает ни запусков, ни
  // графика, ни конверсии; её работа — люди в прогонах и отправленные сообщения. Цифры и
  // журнал считает ОБЩАЯ рука с веб-страницей (chain-page.tsx), своей арифметики тут нет.
  const chain = row?.chain === true || row?.wizardId === CHAIN_WIZARD_ID;

  // ── СТРАНИЦА БОТА: период, график и журнал людьми (П42, владелец 12.09.2026) ──────
  // Столбиков «за неделю» на странице больше нет («два графика об одном»), поэтому пульс
  // ей не нужен вовсе. Один график с капсулой периодов над ним, под ним итог, ниже —
  // журнал СОБЫТИЙ ЛЮДЬМИ вместо журнала запусков.
  const [days, setDays] = useBotPeriod();
  const [step, setStep] = useBotStep();
  // ЦЕПОЧКА — тот же период и шаг, что у бота (раздел Н): график в людях за выбранный срок.
  const chainStats = useChainStats(chain && row ? (row.draftId ?? row.key) : '', days, step);
  const eventsId = win.kind === 'stats' && !bcast && !chain ? (row?.draftId ?? '') : '';
  // ФИЛЬТР ЖУРНАЛА «Контакты | Все» (владелец 14.09.2026), по умолчанию — контакты.
  const [only, setOnly] = useState<EventsOnly>('goal');
  // ВЫБОР СЕРИЙ ГРАФИКА (APP-CLAIMS-1020 ред. 6, П26.1 = П12): живёт, пока окно открыто, переживает перечитку и смену
  // периода; null — ещё не трогали, включён набор «О боте» (`cardPrefs.lines`; у цепочки — «Запуски»).
  const [seriesSel, setSeriesSel] = useState<CardLine[] | null>(null);
  const events = useBotEvents(eventsId, days, only, step);
  // ДАННЫХ ПОКА НЕТ (П130 п.12, владелец 24.09.2026: «сделай, как это обычно делают»): у
  // бота ещё не было ни одного события — график с пустыми осями не рисуем, на его месте
  // карточка со статусом и словами, что здесь появится. Пока цифры едут — не решаем.
  const fresh = !events.loading && events.since === null && events.totals.entered === 0 && events.totals.contacts === 0;
  const words = useBotEventWords(!!row?.goalTag);
  const emptyFacts = useBotEmptyFacts();
  const openDialog = useOpenDialogFromEvent();
  /** Чей профиль открыт страницей стопки ('' — ничей). */
  const [cardId, setCardId] = useState('');
  /** Переписка человека, чей профиль открыт (её открывает кнопка шапки профиля). */
  const [personDialogId, setPersonDialogId] = useState('');
  /** Карточку человека ПЕРЕЧИТЫВАЕМ после каждой записи (метки, поля): иначе снятая на
   *  сервере метка оставалась на экране (владелец 15.09.2026). */
  const [cardVersion, setCardVersion] = useState(0);
  const cardRow = useContactRow(cardId, cardVersion);
  // ЗАРАНЕЕ, ПОКА ОКНО ОТКРЫТО (PREFETCH-TZ §2.1, правило 2): карточки первых людей журнала и запуски бота
  // (журнал и график) — общей очередью прогрева; тап по человеку и вкладка запусков открываются тем же кадром.
  const journalContactIds = (events.rows ?? []).map((r) => r.contactId ?? '').filter(Boolean).join(',');
  useEffect(() => {
    if (journalContactIds) warmContactRows(journalContactIds.split(','));
  }, [journalContactIds]);
  const warmWf = row && !bcast && !chain ? (row.workflowId ?? '') : '';
  useEffect(() => {
    if (warmWf) warmRuns(warmWf, days);
  }, [warmWf, days]);
  // ЦЕПОЧКА (приёмка §2.1, 10.10.2026): при открытии окна — её цифры за период по умолчанию и за выбранный, общей
  // очередью; вкладка цепочки открывается из кэша.
  const warmChainId = row && chain ? (row.draftId ?? row.key) : '';
  useEffect(() => {
    if (!warmChainId) return;
    warmChainStats(warmChainId, EVENT_DAYS_DEFAULT, 'day');
    warmChainStats(warmChainId, days, step);
  }, [warmChainId, days, step]);
  // Лица людей журнала — ТЕМ ЖЕ конвейером, что фото карточек «Ботов» и «Аналитики»
  // (клиент ничего не качает сам): готовое лежит на общем складе кабинета. Лицо открытой
  // карточки просим тем же складом — второго пути к фото в кабинете нет.
  const { photos } = useNativePhotos(
    [
      ...(events.rows ?? []).map((r) => r.avatarUrl),
      ...(chainStats.data?.sends ?? []).map((r) => r.avatarUrl ?? ''),
      cardRow?.avatarUrl ?? '',
    ].filter((u) => !!u),
  );

  // КАРТОЧКА ЧЕЛОВЕКА — ТА ЖЕ, ЧТО В «КОНТАКТАХ» И «ЧАТАХ» (владелец 14.09.2026: «нужно
  // было открыть СУЩЕСТВУЮЩУЮ шторку, ты сделал новую»). Второй карточки человека в
  // кабинете нет: здесь живёт ровно тот же родной лист со всеми его полями и действиями.
  //
  // ЛИСТ ОТКРЫВАЕТ ОБЩИЙ ВОДИТЕЛЬ (`useContactCardHost`), а не эта страница: прошлый раз
  // я позвал только сборщик словаря — лист никто не открывал, и владелец получил
  // «теперь ничего не открывается» (П124 п.1).
  useContactCardHost({
    contact: cardRow,
    photo: cardRow?.avatarUrl ? photos[cardRow.avatarUrl] : '',
    onSaved: () => setCardVersion((v) => v + 1),
    onDeleted: () => setCardId(''),
    onGone: () => setCardId(''),
    onOpenChat: (dialogId) => {
      setCardId('');
      void openChatFromBotWindow(() => openDialog(dialogId));
    },
  });

  // ЖУРНАЛ ЗАПУСКОВ — общая рука с веб-шторкой (второй ленты нет). Пустой ключ = журнала
  // у окна нет вовсе, в сеть не ходим.
  const journalId =
    win.kind === 'runs' ? win.workflowId : win.kind === 'stats' && !chain ? (row?.workflowId ?? '') : '';
  const onlyErrors = win.kind === 'runs' ? win.onlyErrors : false;
  const feed = useRunsFeed(bcast || win.kind === 'runs' ? journalId : '', onlyErrors);
  // Подстраницы окна — детали всех прогонов журнала — заранее (PREFETCH-TZ §3 п.1).
  const runIds = (feed.rows ?? []).map((r) => r.id).join(',');
  useEffect(() => {
    if (runIds) warmRunDetails(runIds.split(','));
  }, [runIds]);
  // Период капсулы — только у страницы бота; отдельное окно «Запуски» как было, за месяц.
  const { data: chart } = useRunsChart(journalId, win.kind === 'stats' && !bcast ? days : 30);
  const detail = useRunDetail(openRunId);

  // СТРАНИЦА БОТА П158 (владелец 26.09.2026): герой «Пока без данных», тумблер «Включён» и
  // «О боте» прямо на странице. Сборка без маркера получает прежние 'facts'/'choice'.
  // Браузер рисует те же страницы (lib/stats-web.ts) и новый вид знает всегда.
  const v2 = nativeMethod('statsBotPage2') || !isNativeApp();
  // «О сценарии»: тот же кэш черновика, что у страницы бота — правки видят один снимок.
  // Черновик «О боте» нужен корню окна вида about, странице about поверх окна бота (П98) и
  // самой странице бота — там теперь те же блоки (П158 п.3).
  const aboutOpen = stack.includes('about');
  const inlineAbout = v2 && win.kind === 'stats' && !bcast;
  const draftId = win.kind === 'about' ? win.draftId : aboutOpen || inlineAbout ? (row?.draftId ?? '') : '';
  const { data: draftData, mutate: mutateDraft } = usePersistentData<{ ok: boolean; draft: DraftSnapshot }>(
    draftId ? `cabinet:scenario-draft:${draftId}` : '',
    draftId ? `/api/cabinet/scenarios/drafts/${draftId}` : '',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const draft = draftData?.draft ?? null;

  // Ответы форм окна (название/описание/категория, имя и вид копии) — состояние ЗДЕСЬ,
  // как у веб-формы: натив только показывает готовые строки и возвращает набранное.
  const [about, setAbout] = useState<{ title: string; desc: string; category: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!draft || about) return;
    setAbout({ title: draft.title, desc: draft.description ?? '', category: draft.category ?? '' });
  }, [draft, about]);

  const dupSuffix = locale === 'en' ? ' — copy' : ' — копия';
  const [dup, setDup] = useState<{ name: string; mode: 'wizard' | 'schema' }>(() => ({
    name: win.kind === 'dup' ? `${win.title.trim()}${dupSuffix}`.slice(0, 120) : '',
    mode: 'wizard',
  }));
  // Дубликат и правка со страницы бота: чей бот и умеет ли он мастер — из строки списка.
  const dupDraftId = win.kind === 'dup' ? win.draftId : (row?.draftId ?? '');
  const dupWizard = win.kind === 'dup' ? win.wizard : !!row?.wizard;
  const rowTitle = row?.title ?? '';
  useEffect(() => {
    const onPage = (e: Event) => {
      const page = (e as CustomEvent<{ page: 'about' | 'dup' }>).detail?.page;
      if (page !== 'about' && page !== 'dup') return;
      if (page === 'dup') setDup({ name: `${rowTitle.trim()}${dupSuffix}`.slice(0, 120), mode: 'wizard' });
      setErr(null);
      setStack([page]);
    };
    window.addEventListener(BOT_WINDOW_PAGE_EVENT, onPage);
    return () => window.removeEventListener(BOT_WINDOW_PAGE_EVENT, onPage);
  }, [rowTitle, dupSuffix]);

  const catNames = catList(cats).map((c) => c.name);
  // ПАПКИ — ТА ЖЕ РУКА, ЧТО «НАСТРОЙКИ → ПАПКИ → БОТЫ» (П158 п.4): добавить в хвост,
  // удалить (боты удалённой папки остаются «без папки»).
  const bf = useBotsFolders();
  const folderNames = bf.cats.map((c) => c.name);

  // ── ТУМБЛЕР «ВКЛЮЧЁН» (П158 п.2) ─────────────────────────────────────────────────
  // Правда — список «Ботов» (тот же кэш): бот — запущен ли готовый сценарий, цепочка — её
  // флаг. Пока попытка идёт, тумблер стоит, куда его повёл палец (`want`), и не нажимается;
  // кончилась — одна свежая выгрузка списка, и тумблер отдаётся правде: отказ или
  // «Отмена» возвращают его на место.
  const scn = usePersistentData<{ ok?: boolean; drafts?: Array<{ id?: string; chainActive?: boolean | null }> }>(
    'cabinet:scenarios',
    '/api/cabinet/scenarios',
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  const realOn = chain
    ? (scn.data?.drafts ?? []).some((d) => d.id === row?.draftId && d.chainActive === true)
    : row?.st === 'run';
  const [want, setWant] = useState<boolean | null>(null);
  const busyFlip = useRef(false);
  const botOn = want ?? realOn;
  const flipBot = async (on: boolean) => {
    const toggle = hooks.toggle;
    if (!toggle || busyFlip.current) return;
    busyFlip.current = true;
    noteBridgeEvent('botPage', `switch:${on ? 'on' : 'off'}`);
    setWant(on);
    try {
      await toggle();
    } catch {
      /* рука сама говорит человеку об отказе — здесь только вернуть тумблер */
    }
    // Свежая правда — штатной перечиткой того же кэша (`usePersistentData`, запрет №5:
    // своих fetch-путей у фронта нет), которым живут список «Ботов» и эта страница.
    scn.reload();
    noteBridgeEvent('botPage', 'switch:done');
    setWant(null);
    busyFlip.current = false;
  };

  // ТУМБЛЕР ЦЕПОЧКИ — ОБЩАЯ РУКА (chain-toggle.tsx): те же четыре разговора, что у
  // карточки в списке (оплата, накопившееся, занятый вход, наша поломка).
  const [, setChainBusy] = useState(false);
  const [chainFail, setChainFail] = useState(false);
  const chainToggle = useChainToggle({
    onBusy: (id) => setChainBusy(id !== null),
    onOk: () => {
      setChainFail(false);
      chainStats.reload();
      hooks.reload?.();
    },
    onFail: () => setChainFail(true),
    // Отказ (лимит бесплатного и т.п.) — родным низким листом, не страницей окна (владелец
    // 18.09.2026: «ошибка должна была быть полушторкой»); старая сборка — прежняя страница.
    note: (title, text) => {
      if (hasNativeNotice()) {
        void showNativeNotice({ style: 'sheet', title, text, haptic: 'error' });
        return;
      }
      openNativeBotWindow({ kind: 'note', title, text }, hooks);
    },
  });

  // ── Куски окон ────────────────────────────────────────────────────────────────────
  /** Линия запусков за выбранный период — ОДИН график страницы (П42). Тот же кусок
   *  служит шапкой журнала запусков в окне «Запуски» и у рассылки. */
  const compact = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const chartBlock = (): NativeStatsBlock => {
    const line = chart && chart.state === 'ok' && chart.runs.length >= 2 ? chart.runs : [];
    const lineErr = line.length > 0 ? (chart?.errors ?? []) : [];
    // ВТОРАЯ ЛИНИЯ — ПОДАРКИ ПО ДНЯМ за тот же период (владелец 12.09.2026: «добавь на
    // график подарки»); длины должны совпасть, иначе линии нет.
    const line2 = line.length > 0 && events.goalDaily.length === line.length ? events.goalDaily : [];
    return {
      kind: 'runs',
      line,
      lineErr,
      ...(line2.length > 0 ? { line2 } : {}),
      // ЦВЕТ ЛИНИИ — ЦВЕТ СЕТИ БОТА (владелец 12.09.2026: «для Телеграм — цвета Телеграма,
      // для Инстаграм — Инстаграма»): тот же hex, что у значка сети в списках.
      ...(row?.trigger ? { lineInk: channelHex(chanOf(row.trigger)) } : {}),
      // ЛЕГЕНДА ЛИНИЙ (владелец 12.09.2026, П68): слова для полосы над графиком — в покое
      // там стоят «Запуски»/«Подарки» с кружками цветов линий, под пальцем — плашка дня.
      // Ключи новые и необязательные (запрет №8): старая сборка их не читает.
      ...(line.length > 0 ? { lineName: to('runsLegendRuns') } : {}),
      ...(line2.length > 0 ? { line2Name: to('runsLegendGifts') } : {}),
      lineMax: line.length > 0 ? String(Math.max(1, ...line)) : '',
      lineMin: line.length > 0 ? String(Math.min(...line)) : '',
      // Плашка «водишь пальцем — меняются циферки» (владелец 2026-07-23): дату каждой
      // точки и слова считает ВЕБ, у натива своих дат и переводов нет.
      tips: line.map((v, i) => {
        const d = new Date(Date.now() - (line.length - 1 - i) * 24 * 60 * 60 * 1000);
        return {
          day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d),
          runs: to('runsTipRuns', { n: v }),
          ...(line2.length > 0 ? { gift: to('runsTipGifts', { n: line2[i] ?? 0 }) } : {}),
          err: (lineErr[i] ?? 0) > 0 ? to('runsTipErr', { n: lineErr[i] ?? 0 }) : '',
        };
      }),
    };
  };

  /** СЕРИИ ГРАФИКА ДЛЯ ВЕБА (APP-CLAIMS-1020 ред. 6, П26.1 = П12): три одиночные серии и включённые сейчас — выбор
   *  окна, а до первого тапа — набор по умолчанию (`cardPrefs.lines` бота / «Запуски» цепочки) из имеющихся. */
  const webSeries = (series: NativeStatsSeries[], byDefault: readonly CardLine[]): { series: NativeStatsSeries[]; seriesOn: string[] } => {
    const have = new Set<string>(series.map((x) => x.key));
    const on = (seriesSel ?? byDefault).filter((k) => have.has(k));
    return { series, seriesOn: on.length > 0 ? on : series[0] ? [series[0].key] : [] };
  };

  /** ОДИН ГРАФИК СТРАНИЦЫ БОТА, ДВЕ ЛИНИИ В ЛЮДЯХ (П94/П113, владелец 14.09.2026):
   *  «Запустили бота» (`line`, роль `--blue`, П10) и «Контакты» (`line2`, роль `--green` —
   *  необязательный `line2Ink`) на одной оси; разрыв между ними — конверсия. В
   *  плашке дня — обе цифры (`tips[].conv` — контакты дня). */
  const dayBlocks = (): NativeStatsBlock[] => {
    const entered = events.enteredDaily.length >= 2 ? events.enteredDaily : [];
    const contacts = events.goalDaily.length === entered.length ? events.goalDaily : [];
    if (entered.length === 0) return [];
    // ДЕНЬ ТОЧКИ — КАЛЕНДАРНЫЙ (владелец 14.09.2026): отступ назад по ДАТАМ, ровно как
    // считает корзины сервер (календарные сутки зоны человека) и как подписан список.
    const dayOf = (n: number, i: number) => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      if (step === 'week') {
        // Точка недели подписывается её понедельником (П119) — как в вебе.
        d.setDate(d.getDate() - (n - 1 - i) * 7);
        d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      } else {
        d.setDate(d.getDate() - (n - 1 - i));
      }
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d);
    };
    // ЦВЕТА СЕРИЙ — КОНСТАНТЫ ПЛИТОК ПАСХАЛКИ, ОДНИ С ТЕЛЕФОНОМ (APP-CLAIMS-1020 ред. 6, П13; `LINE_INK`
    // lib/quick-tiles.ts): запуски — плитка Instagram, контакты — плитка Telegram, конверсия — бирюзовый `--green`.
    const ink2 = LINE_INK.contacts;
    // ТРЕТЬЯ ЛИНИЯ — «Конверсия» (`--accent-stroke`, П10) по своей оси 0…100 (владелец 14.09.2026); подписи
    // над графиком в приложении — выключатели линий (BwLineView). Ключи line3* и `pct`
    // НОВЫЕ и необязательные (запрет №8): старая сборка рисует две линии, как было.
    const conv = events.convDaily.length === entered.length ? events.convDaily : [];
    return [
      {
        kind: 'runs',
        line: entered,
        lineErr: [],
        lineInk: LINE_INK.runs,
        // ЧИСЛА В ПИЛЮЛЯХ — СЖАТЫЕ (владелец 15.09.2026: «предусмотреть тысячи и миллионы,
        // цифры предсказуемых размеров»): «1,2 тыс.», «3,4 млн» / «1.2K», «3.4M».
        lineName: `${t('chartEntered')} · ${compact.format(events.totals.entered)}`,
        ...(contacts.length > 0
          ? { line2: contacts, line2Ink: ink2, line2Name: `${t('chartContacts')} · ${compact.format(events.totals.contacts)}` }
          : {}),
        ...(conv.length > 0
          ? { line3: conv, line3Max: 100, line3Ink: LINE_INK.conv, line3Name: `${t('statsConv')} · ${events.totals.conv} %` }
          : {}),
        lineMax: String(Math.max(1, ...entered, ...contacts)),
        lineMin: '0',
        ...webSeries(
          [
            { key: 'runs', mark: 'bar', word: t('chartEntered'), total: compact.format(events.totals.entered), ink: LINE_INK.runs, points: entered, tips: entered.map((n) => t('tipEntered', { n })) },
            ...(contacts.length > 0
              ? [{ key: 'contacts' as const, mark: 'bar' as const, word: t('chartContacts'), total: compact.format(events.totals.contacts), ink: LINE_INK.contacts, points: contacts, tips: contacts.map((n) => t('tipContacts', { n })) }]
              : []),
            ...(conv.length > 0
              ? [{ key: 'conv' as const, mark: 'line' as const, word: t('statsConv'), total: `${events.totals.conv} %`, ink: LINE_INK.conv, points: conv, tips: conv.map((n) => `${n} %`) }]
              : []),
          ],
          prefs.lines,
        ),
        tips: entered.map((v, i) => ({
          day: dayOf(entered.length, i),
          runs: t('tipEntered', { n: v }),
          err: '',
          ...(contacts.length > 0 ? { conv: t('tipContacts', { n: contacts[i] ?? 0 }) } : {}),
          ...(conv.length > 0 ? { pct: `${conv[i] ?? 0} %` } : {}),
        })),
      },
    ];
  };

  /** Журнал запусков одним куском — тот же состав, что у веб-панели «Запуски». Живёт
   *  ТОЛЬКО в окне «Запуски» и на странице рассылки: на странице бота под графиком
   *  теперь журнал ЛЮДЕЙ (П42), а прогоны с именами служебных шагов человеку не
   *  показываются вовсе. */
  const runsBlock = (): NativeStatsBlock => {
    if (feed.failed) {
      return {
        kind: 'runs',
        empty: to('loadError'),
        // «Повторить» — та же кнопка, что в вебе рядом со строкой отказа.
        more: to('retry'),
        key: 'retry',
      };
    }
    if (feed.rows === null) return { kind: 'runs', loading: true };
    const base = chartBlock();
    if (feed.state !== 'ok' || feed.rows.length === 0) {
      return {
        ...base,
        empty: feed.state === 'unavailable' ? to('jrUnavailable') : onlyErrors ? to('jrErrEmpty') : to('jrEmpty'),
      };
    }
    // Группировка по датам — тем же правилом, что в вебе (ключ = локальные сутки старта).
    const groups: Array<{ day: string; items: NativeStatsRun[] }> = [];
    for (const r of feed.rows) {
      if (!r.startedAt) continue;
      const day = fmtDay(r.startedAt);
      const item: NativeStatsRun = {
        id: r.id,
        title: r.endNode ?? statusLabel[r.status],
        bad: r.status === 'error',
        tag: r.status === 'canceled' || r.status === 'running' ? statusLabel[r.status] : '',
        meta: `${fmtTime(r.startedAt)} · ${modeLabel[r.mode]}${
          r.durationMs !== null ? ` · ${fmtDuration(r.durationMs)}` : ''
        }`,
        why: r.status === 'error' && r.errorText ? (whyText(r.errorText) ?? '') : '',
      };
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(item);
      else groups.push({ day, items: [item] });
    }
    return {
      ...base,
      groups,
      ...(feed.cursor ? { more: feed.moreBusy ? to('jrMoreBusy') : to('jrMore'), key: 'more', busy: feed.moreBusy } : {}),
    };
  };

  /** Цифры страницы бота (или рассылки) — те же пузыри, что у веб-страницы.
   *  У бота слова цифр подбирает ОДНА рука на веб и приложение (lib/bot-stats-words.ts,
   *  П67): у «Кодового слова» — комментарий → подошло → подарок, у «Подарка за подписку»
   *  Telegram — «Старт», у Instagram — «обратились»; у прочих прежние слова. */
  const statsCards = (): NativeStatsCard[] => {
    if (!row) return [];
    // У БОТА ПЛАШЕК ЦИФР НЕТ (владелец 14.09.2026: «они не нужны, потому что на графике всё
    // есть»): контакты и конверсия стоят в подписях-выключателях над графиком, а по дням их
    // показывает подсказка. Прежние две плашки П77 сняты.
    if (!bcast) return [];
    const launch = ctl.state?.launch ?? null;
    // APP-CLAIMS-1020 ред. 6, П20: неотправленная — одна строка «Рассылка не отправлялась» (кусок `note`), пузырей нет.
    if (!launch) return [];
    const reasons = ctl.state?.reasons ?? [];
    return [
      { label: tb('numSent'), value: String(launch.started) },
      { label: tb('numDelivered'), value: String(launch.delivered) },
      {
        label: tb('numFailed'),
        value: String(launch.failed),
        ...(launch.failed > 0 ? { hint: tb('failedHint') } : {}),
        ...(reasons.length > 0
          ? { rows: reasons.map((r) => ({ label: ts(broadcastFailKey(r.reason)), value: String(r.n) })) }
          : {}),
      },
      // П20: «Нажали кнопку» — только если в сообщении есть кнопки, с подписью под строкой.
      ...(ctl.state?.hasButtons ? [{ label: tb('numClicked'), value: String(launch.clicked), hint: tb('clickedHint') }] : []),
    ];
  };

  /** П20: секция «Отправки» — строка на каждую отправку «<дата, время> — отправлено N, доставлено M, не доставлено K»,
   *  тап — ничего (общий протокол строк `fields`, вид `value`). */
  const sendsBlock = (): NativeStatsBlock[] => {
    const list = ctl.state?.launches ?? [];
    if (!bcast || !ctl.state?.launch || list.length === 0) return [];
    const when = (iso: string) =>
      new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
    return [
      {
        kind: 'fields',
        label: tb('sendsTitle'),
        fields: list.map((l) => ({
          key: `send:${l.id}`,
          kind: 'value' as const,
          title: tb('sendRow', { when: when(l.createdAt), sent: l.started, ok: l.delivered, failed: l.failed }),
        })),
      },
    ];
  };

  /** Строки формы — ОБЩИЙ протокол строк «Аккаунта» (второй формы строки в приложении
   *  нет). ПРАВКА НА МЕСТЕ, А НЕ ЛИСТОМ (владелец 13.09.2026, П80: «описание не видно»):
   *  вид `field` — поле прямо в строке, и описание растёт по тексту (`multiline`);
   *  прежний `text` открывал отдельный лист под каждое поле. */
  // ПОЛЯ БЕЗ ПОДПИСИ ВНУТРИ ПУЗЫРЯ (П101, владелец 13.09.2026: «подпись и текст одним видом —
  // не нативно»): подпись несёт заголовок группы, в пузыре — только поле с подсказкой.
  const aboutNameField = (): NativeAccountField => ({
    key: 'title',
    kind: 'field',
    title: '',
    value: about?.title ?? '',
    raw: about?.title ?? '',
    placeholder: ts('aboutTitleLabel'),
    max: 100,
  });

  const aboutDescField = (): NativeAccountField => ({
    key: 'desc',
    kind: 'field',
    title: '',
    value: about?.desc ?? '',
    raw: about?.desc ?? '',
    multiline: true,
    placeholder: ts('aboutDescPh'),
    max: 255,
  });

  // Папки, которой больше нет (удалили в «Настройках» или в листе), у бота не показываем —
  // он «без папки», как и в списке «Ботов» (bots-folders.tsx: ботов удаление не трогает).
  // Два списка — один и тот же набор (руки «Настроек» и прежней «Новой папки»): новое имя
  // может успеть появиться только в одном из них, пока сервер не подтвердил запись.
  // ПАПКИ БОТА — НЕСКОЛЬКО (APP-CLAIMS-1020 ред. 3, П11): выбор живёт локально (`folderPick`) до ответа сервера,
  // запись — сразу, одной ручкой `PUT /drafts/<id>/folders` на обеих страницах (окно и «О боте»). Папки, которой больше
  // нет (удалили в «Настройках» или в листе), у бота не показываем — два списка имён: руки «Настроек» и прежней
  // «Новой папки» (новое имя может успеть появиться только в одном из них).
  const [folderPick, setFolderPick] = useState<string[] | null>(null);
  const knownFolder = (n: string) => folderNames.includes(n) || catNames.includes(n);
  const mineFolders = (folderPick ?? (draft ? draftFolders(draft) : [])).filter(knownFolder);
  const catNow = mineFolders[0] ?? '';
  const catField = (): NativeAccountField => ({
    key: 'category',
    kind: 'choice',
    title: t('aboutCategory'),
    value: mineFolders.length > 0 ? mineFolders.join(', ') : t('aboutCategoryNone'),
    picked: mineFolders.join(','),
    multi: true,
    options: catNames.map((n) => ({ value: n, label: n })),
    // «ДОБАВИТЬ КАТЕГОРИЮ» ПОД СПИСКОМ (П102, владелец 13.09.2026): те же папки, что в
    // «Настройках»; новая встаёт в конец списка и сразу выбирается.
    pickAction: { label: t('catAdd'), value: 'cat:new' },
  });

  /** ЛИСТ ПАПКИ «КАК МЕТКИ» (П158 п.4; П11 — мультивыбор `pickedAll`): слова и списки для листа; сборка без
   *  маркера ключа не знает и открывает прежний выбор `catField` с «Добавить папку». */
  const foldersSheet = (): NativeStatsFolders => ({
    key: 'category',
    title: t('aboutCategory'),
    items: folderNames,
    picked: catNow,
    pickedAll: mineFolders,
    none: t('aboutCategoryNone'),
    add: t('catAdd'),
    addPh: t('catNewPh'),
    addBtn: t('catNewSave'),
    cancel: tct('cancel'),
    del: tf('deleteShort'),
    delAsk: t('catDelQ'),
  });

  /** БЛОКИ «О БОТЕ» — ОДНА РУКА НА ДВЕ СТРАНИЦЫ (П158 п.3): страница «О боте» из «⋯» и
   *  низ страницы бота. `afterDesc` — что встать под «Описанием» (на странице бота это
   *  «Сохранить», пока название или описание правятся). */
  const aboutBlocks = (afterDesc: NativeStatsBlock[] = [], rows = false): NativeStatsBlock[] => [
    // НА СТРАНИЦЕ БОТА — СТРОКАМИ С ПОЛУШТОРКОЙ (владелец 26.09.2026: «нажатие на них
    // вызывает полушторку, где всё настраивается… и у полушторки галочка»): вид `text` —
    // значение в строке, тап открывает родной лист с полем и ✓ (тот же, что у «Имени»
    // контакта, П157); описание — большой лист (`multiline`). Кнопки «Сохранить» нет:
    // ✓ листа и есть запись. На странице «О боте» из «⋯» — поля на месте, как были (П80).
    ...(rows
      ? [
          {
            kind: 'fields' as const,
            fields: [
              {
                key: 'title',
                kind: 'text' as const,
                title: ts('aboutTitleLabel'),
                value: about?.title ?? '',
                raw: about?.title ?? '',
                placeholder: ts('aboutTitleLabel'),
                max: 100,
                // Подпись капсулы листа правки (AccountEditSheet.doneTitle): без неё родной лист
                // рисовал розовую капсулу БЕЗ слова (кадры B09-botwin-name/-desc).
                done: ts('aboutSave'),
                emptyError: ts('aboutError'),
              },
              {
                key: 'desc',
                kind: 'text' as const,
                title: ts('aboutDescLabel'),
                value: about?.desc ?? '',
                raw: about?.desc ?? '',
                placeholder: ts('aboutDescPh'),
                max: 255,
                multiline: true,
                done: ts('aboutSave'),
              },
            ],
            hint: ts('aboutDescHint'),
          },
        ]
      : [
          { kind: 'fields' as const, label: ts('aboutTitleLabel'), fields: [aboutNameField()] },
          { kind: 'fields' as const, label: ts('aboutDescLabel'), fields: [aboutDescField()], hint: ts('aboutDescHint') },
        ]),
    ...afterDesc,
    { kind: 'fields', label: t('aboutCategory'), fields: [catField()], folders: foldersSheet() },
    // Настройки превью — последним блоком страницы: сперва «кто этот бот», потом
    // «как он выглядит в списке» (П119).
    { kind: 'fields', label: t('cardPrefsLabel'), fields: prefsFields(), hint: t('cardPrefsHint') },
  ];

  /** Страницы «О боте» и «Дубликат» — и корнем окна своего вида, и поверх окна бота (П98). */
  const aboutPage = (key: string): NativeStatsPage => ({
    key,
    title: ts('aboutTitle'),
    action: { key: 'save', label: ts('aboutSave'), icon: 'checkmark', ...(busy ? { busy: true } : {}) },
    ...(draft ? {} : { loading: true }),
    blocks: draft ? [...(err ? [{ kind: 'error' as const, text: err }] : []), ...aboutBlocks()] : [],
  });

  /** «О БОТЕ» НА СТРАНИЦЕ БОТА (П158 п.3, владелец 26.09): те же блоки, но название и
   *  описание — строками с полушторкой; ✓ листа пишет сразу той же рукой `saveAbout`
   *  (`stay`), папка и превью — сразу, как на странице «О боте». */
  const inlineAboutBlocks = (): NativeStatsBlock[] => {
    if (!inlineAbout || !draft || !about) return [];
    return aboutBlocks(err ? [{ kind: 'error' as const, text: err }] : [], true);
  };

  /** НАСТРОЙКИ ПРЕВЬЮ КАРТОЧКИ (П119, владелец 14.09.2026): что рисовать, за какой срок и
   *  каким шагом. Живут у бота, правятся здесь же — «О боте» и есть паспорт бота. */
  const prefs = parseCardPrefs(draft?.cardPrefs ?? null);
  // У ЦЕПОЧКИ ЛИНИЯ ОДНА — «Запуски» (владелец 22.09.2026, правило в bot-card-prefs
  // `cardPrefsFor`): выбор линий ей не предлагаем, срок и шаг — как у всех.
  const prefsFields = (): NativeAccountField[] => [
    ...(chain ? [] : [{
      key: 'card:lines',
      kind: 'choice',
      title: t('cardLinesTitle'),
      // РОВНО ЧЕТЫРЕ НАБОРА, ВЫБОР ОДНОГО (APP-CLAIMS-1020 ред. 3, П8, как на телефоне): «Запуски», «Контакты»,
      // «Запуски, Контакты», «Конверсия».
      value: prefs.lines.map((k) => t(`cardLine_${k}`)).join(', '),
      picked: cardSetKey(prefs.lines),
      options: CARD_LINE_SETS.map((set) => ({ value: cardSetKey(set), label: set.map((k) => t(`cardLine_${k}`)).join(', ') })),
    } satisfies NativeAccountField]),
    {
      key: 'card:days',
      kind: 'choice',
      title: t('cardDaysTitle'),
      value: t('cardDays', { n: prefs.days }),
      picked: String(prefs.days),
      options: EVENT_DAYS.map((d) => ({ value: String(d), label: t('cardDays', { n: d }) })),
    },
    // ШАГ ПОЯВЛЯЕТСЯ ТОЛЬКО НА ДЛИННОМ СРОКЕ (владелец: «если 30 дней и больше»): на
    // неделе группировать нечего, и лишняя строка там только путала бы.
    ...(weekAllowed(prefs.days)
      ? [
          {
            key: 'card:step',
            kind: 'choice' as const,
            title: t('cardStepTitle'),
            value: prefs.step === 'week' ? t('cardStepWeek') : t('cardStepDay'),
            picked: prefs.step,
            options: [
              { value: 'day', label: t('cardStepDay') },
              { value: 'week', label: t('cardStepWeek') },
            ],
          },
        ]
      : []),
  ];

  /** Записать настройки превью: та же ручка черновика, что у «О боте», но одним полем —
   *  название и описание при этом не трогаются (сервер меняет только присланное). */
  const saveCardPrefs = async (next: CardPrefs) => {
    if (!draft) return;
    const r = await putScenarioAbout(draft.id, draft.version, {
      title: draft.title,
      description: draft.description ?? null,
      logo: draft.logo ?? null,
      category: draft.category ?? null,
      cardPrefs: next,
    });
    if (r.ok && r.draft) {
      mutateDraft({ ok: true, draft: r.draft });
      // Список «Ботов» держит свои карточки — обновляем и его, иначе превью отстанет.
      hooks.reload?.();
    }
  };

  /** Папки бота — сразу, одной ручкой `PUT /drafts/<id>/folders {folders}` (П11): весь выбор целиком, название и
   *  описание не трогаются. Не записалось — выбор возвращается к сохранённому, человеку — строка ошибки окна. */
  const saveFolders = async (list: string[]) => {
    if (!draft) return;
    const next = [...new Set(list.filter(Boolean))];
    setFolderPick(next);
    const r = await fetch(`/api/cabinet/scenarios/drafts/${encodeURIComponent(draft.id)}/folders`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ folders: next }),
    })
      .then((x) => x.json() as Promise<{ ok?: boolean; folders?: string[] }>)
      .catch(() => null);
    if (r?.ok) {
      const saved = r.folders ?? next;
      mutateDraft({ ok: true, draft: { ...draft, category: saved[0] ?? null, categories: saved } });
      setFolderPick(null);
      hooks.reload?.();
      return;
    }
    setFolderPick(null);
    setErr(t('catSaveErr'));
  };

  /** Тапы листа папки (П158 п.4; П11): включить/выключить папку (лист остаётся), добавить (и сразу включить),
   *  удалить у всех. `pick` — тап прежнего листа одной папки (сборки без `pickedAll`): бот кладётся в неё одну. */
  const onFolderKey = (key: string) => {
    noteBridgeEvent('botPage', key.slice(0, 40));
    if (key.startsWith('about:cat:toggle:')) {
      const name = key.slice('about:cat:toggle:'.length);
      void saveFolders(toggleFolder(mineFolders, name, !mineFolders.includes(name)));
    } else if (key.startsWith('about:cat:pick:')) {
      const name = key.slice('about:cat:pick:'.length);
      void saveFolders(name ? [name] : []);
    } else if (key.startsWith('about:cat:add:')) {
      const name = key.slice('about:cat:add:'.length).trim().slice(0, 40);
      if (!name) return;
      if (!folderNames.includes(name)) bf.save('', name, null);
      void saveFolders(toggleFolder(mineFolders, name, true));
    } else if (key.startsWith('about:cat:del:')) bf.remove(key.slice('about:cat:del:'.length));
  };

  const newCatName = newCat.trim();
  const newCatTaken = catNames.some((n) => n.toLowerCase() === newCatName.toLowerCase());
  const newCatPage = (): NativeStatsPage => ({
    key: 'about:newcat',
    title: t('catNewTitle'),
    action: { key: 'newcat:save', label: t('catNewSave'), icon: 'checkmark', ...(!newCatName || newCatTaken ? { busy: true } : {}) },
    // Единственное поле — каретка и клавиатура сразу (П167 п.2); ключ аддитивный.
    focus: 'newcat',
    blocks: [
      {
        kind: 'fields',
        label: t('catNewLabel'),
        hint: newCatTaken ? t('catNewTaken') : t('catNewHint'),
        fields: [{ key: 'newcat', kind: 'field', title: '', value: newCat, placeholder: t('catNewPh'), max: 40 }],
      },
    ],
  });

  const dupPage = (key: string): NativeStatsPage => ({
    key,
    title: ts('dupTitle'),
    // ПОЛУШТОРКОЙ (владелец 14.09.2026): на странице одно поле имени — на весь экран её
    // растить незачем, пальцем растянуть можно.
    half: true,
    // Слева крестик, не стрелка (П130 п.11, владелец 24.09.2026).
    close: true,
    // Единственное текстовое поле — каретка и клавиатура сразу (П167 п.2); ключ аддитивный.
    focus: 'dupName',
    action: {
      key: 'create',
      label: ts('dupCreate'),
      icon: 'checkmark',
      ...(busy || !dup.name.trim() ? { busy: true } : {}),
    },
    blocks: [
      ...(err ? [{ kind: 'error' as const, text: err }] : []),
      {
        kind: 'fields',
        label: ts('dupName'),
        hint: ts('dupNameHint'),
        fields: [{ key: 'dupName', kind: 'field', title: '', value: dup.name, placeholder: ts('dupName'), max: 120 }],
      },
      ...(dupWizard && !BUILDER_HIDDEN
        ? [
            {
              kind: 'fields' as const,
              label: ts('dupMode'),
              hint: ts('dupModeHint'),
              fields: [
                {
                  key: 'dupMode',
                  kind: 'choice' as const,
                  title: ts('dupMode'),
                  value: dup.mode === 'schema' ? ts('dupModeSchema') : ts('dupModeWizard'),
                  picked: dup.mode,
                  options: [
                    { value: 'wizard', label: ts('dupModeWizard'), hint: ts('dupModeWizardHint') },
                    { value: 'schema', label: ts('dupModeSchema'), hint: ts('dupModeSchemaHint') },
                  ],
                },
              ],
            },
          ]
        : []),
      { kind: 'note', text: ts('dupNote') },
    ],
  });

  // ── Корневая страница окна ────────────────────────────────────────────────────────
  const rootPage = (): NativeStatsPage => {
    if (win.kind === 'runs') {
      return {
        key: ROOT,
        // Заголовок — тот же, что у веб-шторки: «Запуски» либо «Сбои за сутки».
        title: onlyErrors ? to('runsErrTitle') : ts('runsBtn'),
        blocks: [runsBlock()],
      };
    }
    if (win.kind === 'fail') {
      // Окно родное — значит это ПРИЛОЖЕНИЕ: про пополнение баланса тут не говорим
      // вовсе (ТЗ П127.2, запрет №7), только факт «бот остановлен». Слово выбирает та же
      // рука, что и веб-карточка (lib/bot-fail-text.ts).
      const text = t(botFailTextKey(win.fail, true));
      return {
        key: ROOT,
        title: t('failTitle'),
        blocks: [
          { kind: 'note', text },
          // Ход есть только там, где человеку есть что сделать: открыть бота. Кнопки
          // «Пополнить» в приложении не бывает — покупок в приложениях нет (запрет №7).
          ...(win.fail === 'lint' && win.href
            ? [{ kind: 'button' as const, key: 'openbot', label: t('failOpenCta'), tone: 'accent' as const }]
            : []),
        ],
      };
    }
    if (win.kind === 'note') {
      return { key: ROOT, title: win.title, blocks: [{ kind: 'note', text: win.text }] };
    }
    if (win.kind === 'about') return aboutPage(ROOT);
    if (win.kind === 'dup') return dupPage(ROOT);
    if (!row) {
      // Холодный старт без кэша — скелетон той же раскладкой, а не дыра; списка нет
      // вовсе — честное «бота больше нет».
      return rowsLoading
        ? { key: ROOT, title: t('statsTitle'), loading: true, blocks: [] }
        : { key: ROOT, title: t('statsTitle'), blocks: [{ kind: 'note', text: t('statsGone') }] };
    }
    const mode = ctl.state?.mode ?? broadcastModeOfRow(row) ?? 'manual';
    const status = ctl.state?.status ?? 'draft';
    // APP-CLAIMS-1020 ред. 7, Р2 (как отчёт приложения `bcastStatsPage`): отправлена / отправляется / запланирована —
    // ОДНА «Создать дубликат» (тот же лист, что пункт меню); «Отправить» и «Редактировать» — только у неотправленной.
    void mode;
    const bcSent = status === 'done' || status === 'sending' || status === 'scheduled';
    const bcSend = bcast && !bcSent;
    // В браузере руки `edit` у окна нет — кнопку рисует веб-окно пунктом `edit` меню «⋯» (bot-window-web BcastRootBody).
    const bcEdit = bcast && !bcSent && (!!hooks.edit || !isAppClient());
    const bcDup = bcast && bcSent;
    return {
      key: ROOT,
      title: row.title,
      // «⋯» СПРАВА ШАПКИ — ТО ЖЕ МЕНЮ, ЧТО У КАРТОЧКИ (П42): включить/выключить,
      // «Редактировать», «Создать дубликат», «О боте», «Поделиться», удаление. Кнопки
      // «Редактировать» в теле страницы больше нет — владелец 12.09 велел её убрать.
      // Старая сборка поля не знает и «⋯» не рисует: там те же пункты живут на карточке
      // в списке «Ботов», и это единственная запаска (контракт заморожен, запрет №8).
      ...(hooks.menu ? { menu: true } : {}),
      // КРЕСТИК ВМЕСТО СТРЕЛКИ (П130 п.11, владелец 24.09.2026): карточка бота закрывается,
      // а не «возвращается»; страницы поверх неё («О боте», причины) — стрелкой, как были.
      close: true,
      blocks: [
        // ЦЕПОЧКА — СВОЙ СОСТАВ (М1.6): состояние, три цифры и журнал отправок. Ни
        // графика запусков, ни конверсии, ни «Бот ещё ни разу не работал» у неё нет.
        ...(chain ? chainBlocks() : []),
        // У рассылки цифры сверху; у бота — ПОД графиком (П112), см. ниже.
        ...(bcast && ctl.state?.launch ? [{ kind: 'stats' as const, cards: statsCards() }] : []),
        ...(bcast && ctl.state && !ctl.state.launch ? [{ kind: 'note' as const, text: tb('notSentYet') }] : []),
        ...(bcast && ctl.err ? [{ kind: 'error' as const, text: ctl.err }] : []),
        ...(bcSend
          ? [{ kind: 'button' as const, key: 'bc:send', label: tb('send2'), tone: 'accent' as const, busy: ctl.busy }]
          : []),
        ...(bcEdit ? [{ kind: 'button' as const, key: 'bc:edit', label: t('statsEdit'), tone: 'plain' as const }] : []),
        ...(bcDup ? [{ kind: 'button' as const, key: 'bc:dup', label: ts('dupBtn'), tone: 'plain' as const }] : []),
        // РАССЫЛКА (П20): график запусков, «Запуски | Сбои» и журнал прогонов с шагами сняты — отправки строками.
        ...sendsBlock(),
        // ДАННЫХ ПОКА НЕТ (П130 п.12 → П132 п.1): общая пустая карточка всех мастеров.
        // Бот без воркфлоу на сервере данных иметь не может — тоже она.
        ...(!bcast && !chain && (!row.workflowId || fresh) ? emptyStateBlocks(row.st === 'run', false) : []),
        // СТРАНИЦА БОТА: капсула периодов, ОДИН график, строка итога и журнал людьми.
        ...(row.workflowId && !bcast && !chain && !fresh
          ? [
              {
                kind: 'periods' as const,
                // Кнопки периода — по глубине данных бота (П77), не больше пяти.
                items: periodButtons(events.since).map((d) => ({ key: String(d), label: t(`statsP${d}`) })),
                current: String(periodButtons(events.since).includes(days) ? days : periodButtons(events.since)[0]),
              },
              // ШАГ ГРАФИКА (П119) — той же капсулой, и только на сроке от 30 дней.
              ...(weekAllowed(days)
                ? [
                    {
                      kind: 'periods' as const,
                      items: [
                        { key: 'step:day', label: t('cardStepDay') },
                        { key: 'step:week', label: t('cardStepWeek') },
                      ],
                      current: `step:${step}`,
                    },
                  ]
                : []),
              ...dayBlocks(),
              // ЦИФРЫ ПОД ГРАФИКОМ (П112, владелец 14.09.2026); строки итога нет — второй раз.
              { kind: 'stats' as const, cards: statsCards() },
              // ФИЛЬТР ЖУРНАЛА — та же капсула, что у периодов (тот же вид куска 'periods',
              // тот же KSegment веба): «Контакты» по умолчанию | «Все». Тап шлёт 'period:only:<v>'.
              {
                kind: 'periods' as const,
                items: [
                  { key: 'only:goal', label: t('statsContacts') },
                  { key: 'only:all', label: t('evOnlyAll') },
                ],
                current: `only:${only}`,
              },
              eventsBlock(),
            ]
          : []),
      ],
    };
  };

  /**
   * ПУСТОЕ СОСТОЯНИЕ КАРТОЧКИ — ОДНО НА ВСЕ МАСТЕРА (П132 п.1, владелец 25.09.2026): подарок
   * Instagram, подарок Telegram, цепочка и любой другой бот без данных. Состав: «Пока без
   * данных» со статусом ('facts'), строки «Создан» / «Запущен» / «Аккаунт» ('fields' —
   * общий протокол строк «Аккаунта», значок сети возле значения) и одна серая фраза
   * ('note'). Виды кусков сборка знает давно — Swift не менялся. Слова и строки собирает
   * та же рука, что у браузера (bot-journal.tsx, useBotEmptyFacts). Рассылку не касается.
   */
  const emptyStateBlocks = (on: boolean, isChain: boolean): NativeStatsBlock[] => {
    if (!row) return [];
    if (v2) return emptyStateBlocks2(isChain);
    const f = emptyFacts(row, { on, chain: isChain });
    return [
      { kind: 'facts', when: f.title, tags: [{ text: f.status.text, tone: on ? 'ok' : 'plain' }] },
      ...(f.rows.length > 0
        ? [
            {
              kind: 'fields' as const,
              fields: f.rows.map(
                (r): NativeAccountField => ({
                  key: `empty:${r.key}`,
                  kind: 'value',
                  title: r.title,
                  value: r.value,
                  ...(r.channel === 'telegram' ? { valueImage: 'ChanTG' } : r.channel === 'instagram' ? { valueImage: 'ChanIG' } : {}),
                }),
              ),
            },
          ]
        : []),
      // Старая сборка без героя: призыв проверить самому (ТЗ USERTEST-1014 п.9б) — на месте серой фразы.
      { kind: 'note', text: f.call ?? f.note },
    ];
  };

  /**
   * ПУСТОЕ СОСТОЯНИЕ П158 (владелец 26.09.2026) — сборке с маркером `statsBotPage2`:
   * герой «Пока без данных» с подписью «Включён»/«Выключен» (п.1), пузырь строк с тумблером
   * «Включён» первой строкой над «Создан» (п.2), та же серая фраза и ниже «О боте» (п.3).
   * Строки и слова — та же рука `useBotEmptyFacts`, что у прежнего вида.
   */
  const emptyStateBlocks2 = (isChain: boolean): NativeStatsBlock[] => {
    if (!row) return [];
    const f = emptyFacts(row, { on: botOn, chain: isChain });
    const sw: NativeAccountField[] = hooks.toggle
      ? [
          {
            key: 'bot:on',
            kind: 'switch',
            title: t('botOnLabel'),
            value: '',
            on: botOn,
            // Тумблер «бот включён» — правда только с сервера (SERVER_TRUTH плана прогрева, ТЗ HELP-WARM-FIX §1): пока
            // список не подтверждён ответом, место тумблера пустое и нажать его нельзя.
            disabled: want !== null || !scn.confirmed,
            ...(scn.confirmed ? {} : { wait: true }),
          },
        ]
      : [];
    const rowsF: NativeAccountField[] = f.rows.map(
      (r): NativeAccountField => ({
        key: `empty:${r.key}`,
        kind: 'value',
        title: r.title,
        value: r.value,
        ...(r.channel === 'telegram' ? { valueImage: 'ChanTG' } : r.channel === 'instagram' ? { valueImage: 'ChanIG' } : {}),
      }),
    );
    return [
      {
        kind: 'hero',
        hero: {
          icon: 'chart.bar',
          color: readRoleHex('--mut', '#8a8f98'),
          title: f.title,
          // ПОД ЗАГОЛОВКОМ — ЧТО БУДЕТ, А НЕ СОСТОЯНИЕ (владелец 26.09.2026 13:15: «напиши,
          // что графики появятся, когда данные соберутся»); состояние говорит тумблер ниже.
          // ПРИЗЫВ ПРОВЕРИТЬ САМОМУ (ТЗ USERTEST-1014 п.9б): по первому варианту запуска + хвост; входов не
          // знаем — прежняя подпись.
          meta: f.call ?? t('statsEmptySub'),
        },
      },
      ...(sw.length + rowsF.length > 0 ? [{ kind: 'fields' as const, fields: [...sw, ...rowsF] }] : []),
      // Серая фраза под пузырём снята — её заменяет хвост призыва (ТЗ п.9б); без призыва — как была.
      ...(f.call ? [] : [{ kind: 'note' as const, text: f.note }]),
      ...inlineAboutBlocks(),
    ];
  };

  /**
   * СТРАНИЦА ЦЕПОЧКИ (М1.6): состояние, три цифры и журнал последних отправок. Слова и
   * числа — те же, что на веб-странице (chain-page.tsx), вид кусков — прежний набор
   * ('fields' — пузырь строк, 'stats' — цифры, 'runs' — журнал группами по дням):
   * новых видов кусков цепочка не заводит, Swift не менялся.
   */
  const chainBlocks = (): NativeStatsBlock[] => {
    const d = chainStats.data;
    if (!d) return [{ kind: 'runs', loading: true }];
    // ЦЕПОЧКА БЕЗ ДАННЫХ (П132 п.1) — та же пустая карточка, что у всех ботов; первые
    // данные — прежний состав ниже без изменений.
    if (chainIsEmpty(d)) return emptyStateBlocks(d.active, true);
    // СТРАНИЦА ЦЕПОЧКИ = СТРАНИЦА БОТА (владелец 18.09.2026, раздел Н): капсулы периода и
    // шага, ОДИН график в людях (вошли серым, дошли до конца цветом нарисованного,
    // конверсия зелёной по своей оси) и журнал людьми «кто что получил» по дням. Тумблера и
    // плашек цифр на странице нет: включение — в меню «⋯» и в списке «Ботов». Виды кусков
    // прежние (periods / runs / events) — Swift не менялся.
    const periods = periodButtons(d.since ?? null);
    const shownDays = periods.includes(days) ? days : (periods[0] ?? days);
    const entered = (d.enteredDaily ?? []).length >= 2 ? (d.enteredDaily ?? []) : [];
    const done = (d.doneDaily ?? []).length === entered.length ? (d.doneDaily ?? []) : [];
    const conv = (d.convDaily ?? []).length === entered.length ? (d.convDaily ?? []) : [];
    const totals = d.totals ?? { entered: 0, done: 0, sent: 0, conv: 0 };
    const dayOf = (n: number, i: number) => {
      const dd = new Date();
      dd.setHours(12, 0, 0, 0);
      if (step === 'week') {
        dd.setDate(dd.getDate() - (n - 1 - i) * 7);
        dd.setDate(dd.getDate() - ((dd.getDay() + 6) % 7));
      } else {
        dd.setDate(dd.getDate() - (n - 1 - i));
      }
      return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(dd);
    };
    const chart: NativeStatsBlock[] =
      entered.length > 0
        ? [
            {
              kind: 'runs',
              line: entered,
              lineErr: [],
              lineInk: LINE_INK.runs,
              lineName: `${t('chainChartEntered')} · ${compact.format(totals.entered)}`,
              ...(done.length > 0
                ? {
                    line2: done,
                    line2Ink: LINE_INK.contacts,
                    line2Name: `${t('chainChartDone')} · ${compact.format(totals.done)}`,
                  }
                : {}),
              ...(conv.length > 0
                ? { line3: conv, line3Max: 100, line3Ink: LINE_INK.conv, line3Name: `${t('statsConv')} · ${totals.conv} %` }
                : {}),
              lineMax: String(Math.max(1, ...entered, ...done)),
              lineMin: '0',
              // У цепочки окно открывается на «Запусках» (`cardPrefsFor`, как `seriesCurrent` сервера).
              ...webSeries(
                [
                  { key: 'runs', mark: 'bar', word: t('chainChartEntered'), total: compact.format(totals.entered), ink: LINE_INK.runs, points: entered, tips: entered.map((n) => t('chainTipEntered', { n })) },
                  ...(done.length > 0
                    ? [{ key: 'contacts' as const, mark: 'bar' as const, word: t('chainChartDone'), total: compact.format(totals.done), ink: LINE_INK.contacts, points: done, tips: done.map((n) => t('chainTipDone', { n })) }]
                    : []),
                  ...(conv.length > 0
                    ? [{ key: 'conv' as const, mark: 'line' as const, word: t('statsConv'), total: `${totals.conv} %`, ink: LINE_INK.conv, points: conv, tips: conv.map((n) => `${n} %`) }]
                    : []),
                ],
                ['runs'],
              ),
              tips: entered.map((v, i) => ({
                day: dayOf(entered.length, i),
                runs: t('chainTipEntered', { n: v }),
                err: '',
                ...(done.length > 0 ? { conv: t('chainTipDone', { n: done[i] ?? 0 }) } : {}),
                ...(conv.length > 0 ? { pct: `${conv[i] ?? 0} %` } : {}),
              })),
            },
          ]
        : [];
    const empty = d.sent === 0 && d.sends.length === 0;
    // Журнал людьми — теми же группами по дням и той же строкой, что журнал бота.
    const lines = d.sends.map((row2) => {
      const title = chainSendName(row2, t('chainPageNoName'));
      return {
        key: row2.contactId ? `person:${row2.contactId}|` : '',
        contactId: row2.contactId,
        title,
        text: [row2.n > 0 ? t('chainPageMsg', { n: row2.n }) : '', row2.ok ? '' : t('chainPageNotOk')]
          .filter(Boolean)
          .join(' · '),
        time: fmtTime(row2.at),
        avatarUrl: row2.avatarUrl ?? '',
        at: row2.at,
      };
    });
    const groups = groupBotEvents(lines, fmtDay).map((g) => ({
      day: g.day,
      items: g.items.map(
        (it): NativeStatsEvent => ({
          key: it.key,
          menuKey: it.contactId ? `evmenu:${it.contactId}|` : '',
          photo: it.avatarUrl ? (photos[it.avatarUrl] ?? '') : '',
          initial: contactInitial(it.title),
          initialColor: circleColor(it.title),
          title: it.title,
          text: it.text,
          time: it.time,
        }),
      ),
    }));
    return [
      {
        kind: 'periods',
        items: periods.map((n) => ({ key: String(n), label: t(`statsP${n}`) })),
        current: String(shownDays),
      },
      ...(weekAllowed(shownDays)
        ? [
            {
              kind: 'periods' as const,
              items: [
                { key: 'step:day', label: t('cardStepDay') },
                { key: 'step:week', label: t('cardStepWeek') },
              ],
              current: `step:${step}`,
            },
          ]
        : []),
      ...(chainFail ? [{ kind: 'error' as const, text: to('loadError') }] : []),
      ...chart,
      ...(empty
        ? [
            {
              kind: 'events' as const,
              empty: d.active ? t('chainPageEmpty') : `${t('chainPageEmpty')} ${t('chainPageEmptyOff')}`,
            },
          ]
        : [{ kind: 'events' as const, groups }]),
    ];
  };

  /** ЖУРНАЛ ЛЮДЬМИ (П42): кто написал боту и кто дошёл до подарка, группами по дням.
   *  Строки собирает ТА ЖЕ рука, что рисует их в браузере (bot-journal.tsx). */
  const eventsBlock = (): NativeStatsBlock => {
    // Отказ сети — та же кнопка под пустой строкой, что у журнала запусков, и ТОТ ЖЕ
    // ключ тапа: второго ключа у куска нет, чтобы натив не гадал, какой прислать.
    if (events.failed) {
      return { kind: 'events', empty: to('loadError'), more: to('retry'), key: 'events:more' };
    }
    if (events.rows === null) return { kind: 'events', loading: true };
    if (events.rows.length === 0) {
      return {
        kind: 'events',
        empty: t(only === 'goal' ? 'evEmptyContacts' : 'evEmpty'),
        // APP-CLAIMS-1020 ред. 7, Р7: пустая лента — героем (значок «двое», заголовок, подпись), как `events.hero` страницы
        // приложения. Только веб-окну: старым сборкам моста ключ не шлём — у них лента пустой строкой, как было.
        ...(isAppClient()
          ? {}
          : { hero: { icon: 'person.2', color: '', title: t(only === 'goal' ? 'evEmptyContactsTitle' : 'evEmptyTitle'), meta: t('evEmptyHint') } }),
      };
    }
    const groups = words.groupsOf(events.rows).map((g) => ({
      day: g.day,
      items: g.items.map(
        (it): NativeStatsEvent => ({
          key: it.key,
          // МЕНЮ ДОЛГОГО НАЖАТИЯ (владелец 14.09.2026): «Перейти в чат» и «Посмотреть
          // профиль». Слова и ключи — от веба, натив только показывает лист с вибрацией.
          // КЛЮЧ МЕНЮ ДОЛГОГО НАЖАТИЯ (владелец 14.09.2026): натив вернёт его с рамой
          // пальца, а пункты покажет веб — родным меню, как у карточки бота.
          menuKey: it.contactId || it.dialogId ? `evmenu:${it.contactId}|${it.dialogId}` : '',
          // Лицо готовым снимком — клиент ничего не качает сам (общий склад лиц кабинета).
          photo: it.avatarUrl ? (photos[it.avatarUrl] ?? '') : '',
          initial: it.initial,
          initialColor: circleColor(it.title),
          title: it.title,
          text: it.text,
          time: it.time,
        }),
      ),
    }));
    return {
      kind: 'events',
      groups,
      ...(events.cursor
        ? { more: events.moreBusy ? to('jrMoreBusy') : to('jrMore'), key: 'events:more', busy: events.moreBusy }
        : {}),
    };
  };

  // ── Страницы деталей прогона (каталог: тап открывает страницу мгновенно) ──────────
  const detailPages = (): NativeStatsPage[] =>
    (feed.rows ?? []).map((r): NativeStatsPage => {
      const key = `run:${r.id}`;
      if (open !== key) return { key, title: fmtWhen(r.startedAt), blocks: [] };
      const d = detail.data;
      if (detail.failed || d?.state === 'unavailable') {
        return {
          key,
          title: fmtWhen(r.startedAt),
          blocks: [
            { kind: 'note', text: to('jrdError') },
            { kind: 'button', key: 'runRetry', label: to('retry') },
          ],
        };
      }
      if (!d) return { key, title: fmtWhen(r.startedAt), loading: true, blocks: [] };
      return {
        key,
        title: fmtWhen(r.startedAt),
        blocks: [
          {
            kind: 'facts',
            when: fmtWhen(d.startedAt ?? null),
            tags: [
              ...(d.status ? [{ text: statusLabel[d.status], tone: TAG_TONE[d.status] ?? 'plain' }] : []),
              ...(d.mode ? [{ text: modeLabel[d.mode], tone: 'plain' }] : []),
              ...(d.durationMs !== null && d.durationMs !== undefined
                ? [{ text: fmtDuration(d.durationMs), tone: 'plain' }]
                : []),
            ],
          },
          // Причина — словами (запрет №7): сырой текст движка клиенту не показывается.
          ...(d.errorText ? [{ kind: 'error' as const, text: whyText(d.errorText) ?? '' }] : []),
          ...(d.state === 'gone' ? [{ kind: 'note' as const, text: to('jrdGone') }] : []),
          ...(d.state === 'ok' && (d.steps?.length ?? 0) === 0
            ? [{ kind: 'note' as const, text: to('jrdNoSteps') }]
            : []),
          ...(d.state === 'ok' && (d.steps?.length ?? 0) > 0
            ? [
                {
                  kind: 'steps' as const,
                  steps: (d.steps ?? []).map((s) => ({
                    name: s.name,
                    status: s.status,
                    dur: s.durationMs !== null ? fmtStepDuration(s.durationMs) : '',
                    why: s.error ? (whyText(s.error) ?? '') : '',
                    file: s.hasFile ? to('jrdFile') : '',
                    sample: s.sample ?? '',
                    sampleLabel: to('jrdSample'),
                  })),
                },
              ]
            : []),
        ],
      };
    });

  // Страницы пересобираются на каждый рендер мозга — это и есть «свежие данные».
  // Отсекать повторы памятью тут не нужно: водитель шлёт нативу только НАСТОЯЩУЮ
  // разницу словаря (сравнение отпечатка в useNativeStats), а не каждый чих рендера.
  const pages: NativeStatsPage[] = [
    rootPage(),
    ...detailPages(),
    ...(win.kind !== 'about' && aboutOpen ? [aboutPage('about')] : []),
    ...(stack.includes('about:newcat') ? [newCatPage()] : []),
    ...(win.kind !== 'dup' && stack.includes('dup') ? [dupPage('dup')] : []),
  ];

  // ── Сохранение и создание — ОБЩИМИ руками с вебом ─────────────────────────────────
  /** `next` — значения, которых в `about` ещё нет (ответ полушторки прилетает раньше, чем
   *  состояние обновится); `stay` — страница бота (П158 п.3): после записи остаёмся на месте. */
  const saveAbout = async (next?: Partial<{ title: string; desc: string }>, stay = false) => {
    if (!draft || !about || busy) return;
    const cur = { ...about, ...next };
    const nm = cur.title.trim();
    if (!nm) {
      setErr(ts('aboutError'));
      return;
    }
    setBusy(true);
    setErr(null);
    const r = await putScenarioAbout(draft.id, draft.version, {
      title: nm,
      description: cur.desc.trim() || null,
      // Картинку окно не трогает — сервер меняет только присланные поля.
      logo: draft.logo ?? null,
      // Папки пишет своя ручка (П11): здесь — сохранённая первая, сервер набор не трогает.
      category: draft.category ?? null,
    });
    setBusy(false);
    if (r.ok && r.draft) {
      mutateDraft({ ok: true, draft: r.draft });
      hooks.reload?.();
      if (stay) return;
      // Своё окно «О боте» — закрываем; страница поверх окна бота — назад к аналитике (П98).
      if (win.kind === 'about') closeNativeBotWindow();
      else {
        setStack((st) => st.filter((k) => k !== 'about'));
        setAbout(null);
      }
      return;
    }
    setErr(ts('aboutError'));
  };

  /** Новая категория (П102): в список пользователя (в конец) и в черновик «О боте». */
  const saveNewCat = () => {
    if (!newCatName || newCatTaken) return;
    setCatList([...catNames, newCatName]);
    // Новая папка сразу включается у бота, другие его папки остаются (П11).
    void saveFolders(toggleFolder(mineFolders, newCatName, true));
    setNewCat('');
    setStack((st) => st.filter((k) => k !== 'about:newcat'));
  };

  const createDup = async () => {
    if (!dupDraftId || busy || !dup.name.trim()) return;
    setBusy(true);
    setErr(null);
    const r = await postScenarioDuplicate(dupDraftId, {
      title: dup.name.trim(),
      mode: dupWizard ? dup.mode : 'schema',
      locale: locale === 'en' ? 'en' : 'ru',
    });
    setBusy(false);
    if (r?.ok && typeof r.id === 'string') {
      hooks.fresh?.(r.id);
      hooks.reload?.();
      closeNativeBotWindow();
      return;
    }
    // Кап черновиков — про самого человека; всё остальное наша поломка: текст без
    // подробностей, алярм владельцу (запрет №7 CLAUDE.md).
    if (r?.error === 'too_many_drafts') {
      setErr(ts('dupErrTooMany'));
      return;
    }
    setErr(ts('dupErr'));
    reportUiError('draft_duplicate_failed', `дубликат бота не создался: ${r?.error ?? 'unknown'}`, {
      draftId: dupDraftId,
    });
  };

  useNativeStats({
    // ПЕРВЫЙ КАДР ЖДЁТ ПОЛНЫЕ ДАННЫЕ (правило волны): каталог страниц уже собран, окно
    // поднимается со своим содержимым, а не с пустотой и наездом.
    ready: true,
    theme: themeAttr,
    pages,
    stack: [ROOT, ...stack],
    onTap: (key, rect) => {
      if (key.startsWith('run:')) {
        setStack([key]);
        return;
      }
      // «⋯» шапки — родное меню карточки у рамы кнопки (П42). Само меню показывает
      // раздел: состав пунктов и их руки живут там, второго меню карточки нет.
      if (key === 'menu') {
        hooks.menu?.(rect);
        return;
      }
      // ДОЛГОЕ НАЖАТИЕ НА ЧЕЛОВЕКА — РОДНОЕ МЕНЮ У ПАЛЬЦА (владелец 14.09.2026: «долгое
      // нажатие должно воспроизводить выпадашку, как долгое нажатие на бота на странице
      // „Боты“»). Тот же приём, что у карточки бота: натив прислал раму места нажатия, а
      // меню поднимает веб — второго меню в кабинете нет.
      if (key.startsWith('evmenu:')) {
        const [contactId = '', dialogId = ''] = key.slice('evmenu:'.length).split('|');
        void nativeBridge()
          ?.pointMenu?.({
            items: [
              ...(contactId ? [{ key: 'profile', label: t('evOpenProfile'), icon: 'person.crop.circle' }] : []),
              ...(dialogId
                ? [{ key: 'chat', label: tct('openChat'), icon: 'bubble.left.and.bubble.right', sub: t('evOpenChatHint') }]
                : []),
            ],
            x: rect.x,
            y: rect.y,
            w: rect.w,
            h: rect.h,
            theme: themeAttr,
          })
          .then((res) => {
            if (res?.key === 'profile' && contactId) {
              setPersonDialogId(dialogId);
              setCardId(contactId);
            }
            if (res?.key === 'chat' && dialogId) void openChatFromBotWindow(() => openDialog(dialogId));
          })
          .catch(() => {});
        return;
      }
      // Капсула периодов: перезапрашиваются и график, и журнал, и итог под графиком.
      // Шаг графика (П119): та же капсула, ключ 'period:step:<day|week>'.
      if (key.startsWith('period:step:')) {
        setStep(key.slice('period:step:'.length) === 'week' ? 'week' : 'day');
        return true;
      }
      // ПИЛЮЛЯ СЕРИИ ГРАФИКА (П26.1 = П12): включить/выключить; последнюю включённую не снимаем.
      if (key.startsWith('series:')) {
        const k = key.slice('series:'.length) as CardLine;
        const order: CardLine[] = ['runs', 'contacts', 'conv'];
        const now = (seriesSel ?? (chain ? (['runs'] as CardLine[]) : prefs.lines)).filter((x) => order.includes(x));
        if (now.includes(k)) {
          if (now.length <= 1) {
            journalLog('bot.window.series.last', { key: k, note: 'окно бота: последнюю серию не снимаем' });
            return;
          }
          setSeriesSel(now.filter((x) => x !== k));
        } else {
          setSeriesSel(order.filter((x) => x === k || now.includes(x)));
        }
        journalLog('bot.window.series', { key: k, on: !now.includes(k) });
        return;
      }
      if (key.startsWith('period:only:')) {
        const v = key.slice('period:only:'.length);
        if (v === 'goal' || v === 'all') setOnly(v);
        return;
      }
      if (key.startsWith('period:')) {
        const n = Number(key.slice('period:'.length));
        if (EVENT_DAYS.includes(n as EventDays)) setDays(n as EventDays);
        return;
      }
      // Один ключ на «Показать ещё» и «Повторить»: что именно делать, знает лента.
      if (key === 'events:more') {
        if (events.failed) events.retry();
        else void events.loadMore();
        return;
      }
      // Строка человека — переписка с ним ТЕМ ЖЕ путём, что «Открыть переписку» в
      // «Контактах». Окно закрываем первым: оно системный лист, и родной экран чата
      // поверх уезжающего листа UIKit отбрасывает (Н45).
      if (key.startsWith('dialog:')) {
        const dialogId = key.slice('dialog:'.length);
        void openChatFromBotWindow(() => openDialog(dialogId));
        return;
      }
      // КАРТОЧКА ЧЕЛОВЕКА — ПОВЕРХ ОКНА БОТА (владелец 14.09.2026: «тап по человеку должен
      // открывать стопку СВЕРХУ текущей стопки»; прежний переход уводил в раздел
      // «Контакты» и менял вкладку — «выпадашки вообще нету»). Окно бота остаётся под ней.
      if (key === 'person:chat') {
        const dialogId = personDialogId;
        if (dialogId) void openChatFromBotWindow(() => openDialog(dialogId));
        return;
      }
      if (key.startsWith('person:')) {
        const [contactId = '', dialogId = ''] = key.slice('person:'.length).split('|');
        setPersonDialogId(dialogId);
        setCardId(contactId);
        return;
      }
      if (key === 'more') {
        void feed.loadMore();
        return;
      }
      if (key === 'retry') {
        feed.retry();
        return;
      }
      if (key === 'runRetry') {
        detail.retry();
        return;
      }
      if (key === 'openbot' && win.kind === 'fail' && win.href) {
        const href = win.href;
        void closeBotWindowThen(() => hooks.goScn?.(href));
        return;
      }
      if (key.startsWith('about:cat:')) {
        onFolderKey(key);
        return;
      }
      if (key === 'chain:active') {
        const d = chainStats.data;
        if (d && row) void chainToggle.run({ id: row.draftId ?? row.key, active: d.active });
        return;
      }
      if (key === 'bc:send') {
        void ctl.act('send');
        return;
      }
      if (key === 'bc:stop') {
        void ctl.act('stop');
        return;
      }
      if (key === 'bc:edit') {
        hooks.edit?.();
        return;
      }
      // Р2 ред. 7: «Создать дубликат» отчёта — тот же лист, что пункт меню «⋯» (страница `dup` этого же окна).
      if (key === 'bc:dup') {
        pushNativeBotWindowPage('dup');
        return;
      }
      if (key === 'save') void saveAbout();
      else if (key === 'create') void createDup();
      else if (key === 'newcat:save') saveNewCat();
    },
    onSet: (key, value) => {
      // Тумблер «Включён» страницы бота (П158 п.2): та же рука, что список «Ботов».
      if (key === 'bot:on') {
        void flipBot(value === '1');
        return;
      }
      if (key === 'title' || key === 'desc') {
        const patch = key === 'title' ? { title: value } : { desc: value };
        setAbout((p) => (p ? { ...p, ...patch } : p));
        // Страница бота (П158 п.3): слово пришло с ✓ полушторки — пишем сразу, остаёмся.
        if (inlineAbout && !aboutOpen) void saveAbout(patch, true);
      }
      else if (key === 'category' && value === 'cat:new') {
        setNewCat('');
        setStack((st) => [...st.filter((k) => k !== 'about:newcat'), 'about:newcat']);
      } else if (key === 'category') {
        // П11: выбор нескольких — весь набор строкой через запятую, пишется сразу.
        void saveFolders(value.split(',').map((x) => x.trim()).filter(Boolean));
      }
      // НАСТРОЙКИ ПРЕВЬЮ (П119) сохраняются СРАЗУ, без «Готово»: это не текст, который
      // человек дописывает, а переключатели — и карточка обязана меняться на глазах.
      else if (key === 'card:lines' || key === 'card:days' || key === 'card:step') {
        const next = { ...prefs };
        if (key === 'card:lines') next.lines = value.split(',').map((x) => x.trim()).filter(Boolean) as CardLine[];
        if (key === 'card:days') next.days = Number(value) as CardPrefs['days'];
        if (key === 'card:step') next.step = value === 'week' ? 'week' : 'day';
        void saveCardPrefs(parseCardPrefs(next));
      }
      else if (key === 'newcat') setNewCat(value);
      else if (key === 'dupName') setDup((p) => ({ ...p, name: value }));
      else if (key === 'dupMode') setDup((p) => ({ ...p, mode: value === 'schema' ? 'schema' : 'wizard' }));
    },
    onBack: (key) => {
      // Пустой ключ или корень — закрыли окно целиком: мозг уходит, экран уже уехал.
      if (!key || key === ROOT) {
        closeNativeBotWindow();
        return;
      }
      // Ушли со страницы «О боте» — черновик бросаем: следующий вход берёт свежее.
      if (key === 'about') {
        setAbout(null);
        setErr(null);
      }
      setStack((s) => s.filter((k) => k !== key));
    },
  });

  // Своей разметки у родных окон нет: экран рисует натив. Единственное исключение —
  // шторка «такой бот уже есть» тумблера цепочки: в приложении её показывает родной лист
  // (askNativeBotExists), а на сборке без него — тот же портал в `.cab`, что в вебе.
  return <>{chainToggle.sheet}</>;
}

/** Тон пилюли статуса прогона — тот же смысл, что у класса .tag в вебе (TAG_CLASS). */
const TAG_TONE: Record<string, string> = {
  ok: 'ok',
  error: 'err',
  running: 'warn',
  canceled: 'mut',
};
